// Client import from Excel / CSV (Oct 2026) — used by the setup wizard and by Data & safety. File or pasted rows →
// column mapping (first guess from the header row) → preview: new · already in AVRASTRO · repeated · no name →
// one transaction (Ctrl+Z undoes the whole import). Nothing existing is changed or merged.
import { useMemo, useState } from 'react';
import { CLIENT_IMPORT_FIELDS, guessClientMapping, importClients, parseDelimited, previewClientImport, readTabular, type ClientImportField, type ClientMapping, type ClientRowPreview } from '@vero/domain';
import { useRuntime } from '../core/runtime.tsx';
import { Modal, Seg } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate, personPath } from '../core/router.ts';

export function ClientImport({ onDone }: { onDone?: (count: number) => void }) {
  const rt = useRuntime();
  const { t } = rt;
  const [grid, setGrid] = useState<string[][]>([]);
  const [header, setHeader] = useState(true);
  const [map, setMap] = useState<ClientMapping>({});
  const [fileName, setFileName] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [stage, setStage] = useState<'client' | 'prospect'>('client');
  const [extra, setExtra] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const load = (g: string[][]) => {
    setGrid(g); setExtra(new Set());
    const guess = guessClientMapping(g[0] ?? []);
    const hasHeader = Object.keys(guess).length > 0;
    setHeader(hasHeader);
    setMap(hasHeader ? guess : { displayName: 0, phone: 1, email: 2 });
  };
  const preview = useMemo(() => previewClientImport(rt.view, grid, map, rt.today, { hasHeader: header }), [rt.view, grid, map, header, rt.today]);
  const cols = Math.max(0, ...grid.slice(0, 5).map((r) => r.length));
  const columnOf = (i: number): ClientImportField | '' => (Object.entries(map).find(([, idx]) => idx === i)?.[0] as ClientImportField | undefined) ?? '';
  const setColumn = (i: number, f: ClientImportField | '') => {
    const next: ClientMapping = {};
    for (const [k, idx] of Object.entries(map) as [ClientImportField, number][]) if (idx !== i && k !== f) next[k] = idx;
    if (f) next[f] = i;
    setMap(next);
  };
  const addable = preview.counts.new + extra.size;
  const run = async () => {
    setBusy(true);
    const cs = await rt.run((v, c) => importClients(v, preview, c, { stage, includeExisting: [...extra] }), 'cimport.done', { count: addable });
    setBusy(false);
    if (cs) { rt.close(); onDone?.(addable); }
  };
  const label = (r: ClientRowPreview) => t(`cimport.kind.${r.kind}`);
  return (
    <Modal wide title={t('cimport.title')} footer={<>
      <button className="btn" onClick={rt.close}>{t('setup.cancel')}</button>
      <button className="btn btn-primary" disabled={!addable || busy} onClick={() => void run()} data-testid="cimport-run">{t('cimport.run', { count: addable })}</button>
    </>}>
      <div className="stack" style={{ gap: 10 }} data-testid="client-import">
        <p className="muted small" style={{ margin: 0 }}>{t('cimport.intro')}</p>
        <div className="row wrap" style={{ gap: 8 }}>
          <label className="btn btn-sm" data-testid="cimport-file"><Icon name="upload" />{t('cimport.choose')}<input type="file" hidden accept=".csv,.tsv,.txt,.xlsx" onChange={async (e: { target: HTMLInputElement }) => {
            const f = e.target.files?.[0]; if (!f) return; setErr(null); setFileName(f.name);
            try { load(await readTabular(f.name, new Uint8Array(await f.arrayBuffer()))); } catch { setErr(t('cimport.unreadable')); }
          }} /></label>
          {fileName && <span className="small muted">{fileName}</span>}
        </div>
        {!fileName && <textarea className="textarea" rows={5} placeholder={t('cimport.paste')} aria-label={t('cimport.paste')} onChange={(e: { target: HTMLTextAreaElement }) => load(parseDelimited(e.target.value))} data-testid="cimport-paste" />}
        {err && <p className="callout crit small" role="alert" style={{ margin: 0 }}>{err}</p>}
        {cols > 0 && <div className="mapping" data-testid="cimport-mapping">
          <label className="check"><input type="checkbox" checked={header} onChange={(e: { target: HTMLInputElement }) => setHeader(e.target.checked)} />{t('cimport.header_row')}</label>
          <div className="mapping-grid" style={{ ['--cols' as string]: cols }}>{Array.from({ length: cols }, (_, i) => (
            <div key={i} className="stack" style={{ gap: 4 }}>
              <span className="tiny muted one-line">{header ? grid[0]?.[i] || `#${i + 1}` : `#${i + 1}`}</span>
              <select className="select" value={columnOf(i)} onChange={(e: { target: HTMLSelectElement }) => setColumn(i, e.target.value as ClientImportField | '')} aria-label={t('cimport.column', { n: i + 1 })}>
                <option value="">{t('cimport.ignore')}</option>
                {CLIENT_IMPORT_FIELDS.map((f) => <option key={f} value={f}>{t(`cimport.field.${f}`)}</option>)}
              </select>
            </div>))}</div>
        </div>}
        {preview.rows.length > 0 && <>
          <div className="row wrap" style={{ gap: 6 }} aria-live="polite">
            {(['new', 'existing', 'repeat', 'invalid'] as const).map((k) => <span key={k} className={`chip ${k === 'new' ? 'ok' : k === 'existing' ? 'high' : k === 'invalid' ? 'crit' : ''}`}>{t(`cimport.kind.${k}`)} · {preview.counts[k]}</span>)}
          </div>
          <div className="table-wrap" style={{ maxHeight: 280, overflow: 'auto' }}>
            <table className="table"><thead><tr><th>{t('cimport.col.status')}</th><th>{t('cimport.field.displayName')}</th><th>{t('cimport.field.phone')}</th><th>{t('cimport.field.email')}</th><th>{t('cimport.col.notes')}</th></tr></thead>
              <tbody>{preview.rows.slice(0, 300).map((r) => (
                <tr key={r.line} data-kind={r.kind}>
                  <td className={r.kind === 'new' ? 'dup-unique' : r.kind === 'existing' ? 'dup-client' : 'dup-dupe'}>
                    {r.kind === 'existing'
                      ? <label className="check small"><input type="checkbox" checked={extra.has(r.line)} onChange={(e: { target: HTMLInputElement }) => { const n = new Set(extra); if (e.target.checked) n.add(r.line); else n.delete(r.line); setExtra(n); }} aria-label={t('cimport.add_anyway', { name: r.name })} />● {label(r)}</label>
                      : <>● {label(r)}</>}
                  </td>
                  <td>{r.name || <span className="muted">—</span>}</td>
                  <td className="small">{r.phone ?? ''}</td>
                  <td className="small">{r.email ?? ''}</td>
                  <td className="small muted">
                    {r.matchId && <button type="button" className="linklike small" onClick={() => { rt.close(); navigate(personPath(r.matchId!)); }}>{t(`cimport.match.${r.matchOn}`, { name: rt.view.get('persons', r.matchId)?.displayName ?? '' })}</button>}
                    {r.problems.filter((p) => p !== 'name').map((p) => <span key={p} className="chip high" style={{ marginInlineStart: 4 }}>{t(`cimport.problem.${p}`)}</span>)}
                  </td>
                </tr>))}</tbody></table>
          </div>
          {preview.rows.length > 300 && <p className="tiny muted" style={{ margin: 0 }}>{t('cimport.more_rows', { count: preview.rows.length - 300 })}</p>}
          <div className="field" role="group" aria-label={t('cimport.as')}><span>{t('cimport.as')}</span><Seg values={['client', 'prospect'] as const} value={stage} onChange={setStage} label={(x) => t(`cimport.stage.${x}`)} testId="cimport-stage" /></div>
          <p className="tiny muted" style={{ margin: 0 }}>{t('cimport.safety')}</p>
        </>}
      </div>
    </Modal>
  );
}

/** the card on Data & safety */
export function ClientImportCard() {
  const rt = useRuntime();
  const { t } = rt;
  return (
    <section className="card card-pad" data-testid="client-import-card">
      <div className="row spread wrap" style={{ gap: 12 }}>
        <div className="grow" style={{ minWidth: 220 }}><h3 style={{ margin: '0 0 4px' }}>{t('cimport.card_title')}</h3><p className="muted small" style={{ margin: 0 }}>{t('cimport.card_sub')}</p></div>
        <button className="btn btn-primary" onClick={() => rt.open(<ClientImport />)} data-testid="cimport-open"><Icon name="upload" />{t('cimport.open')}</button>
      </div>
    </section>
  );
}
